import React, { useContext } from "react";
import { HashLink as Link } from "react-router-hash-link";
import { isNil } from "ramda";

import { AffinityFormContext } from "affinityForm";
import { ExecuteButton } from "affinityForm/components/executeButton";
import { Summary } from "affinityUI/data/summary";
import { Table } from "affinityUI/data/table";
import { DateTime, NhsNumber } from "affinityUI/data/valueDisplay";
import { ButtonVariants, NavigateButton } from "affinityUI/elements/button";
import { Card } from "affinityUI/elements/card";
import { VisuallyHidden } from "affinityUI/elements/visuallyHidden";
import { ButtonGroup } from "affinityUI/form/components";
import { dateTimeToTimestamp } from "lib/helpers";
import type { PatientData } from "types/patient";

import type { ResultFormdata } from "../types";

export const ResultsEditConfirmation: React.FC<{
  patient: Omit<PatientData, "consent">;
}> = ({ patient }) => {
  const formContext = useContext(AffinityFormContext);
  const manualResult = formContext?.formData as Maybe<ResultFormdata>;

  if (isNil(manualResult)) {
    return <></>;
  }
  const timestamp = dateTimeToTimestamp(manualResult);

  return (
    <Card>
      <Summary.Section title="Patient details">
        <Summary.List>
          <Summary.Item label="Hospital number">{patient.hospitalNumber}</Summary.Item>
          <Summary.Item label="NHS number">
            <NhsNumber value={patient.nhsNumber} />
          </Summary.Item>
        </Summary.List>
      </Summary.Section>
      <Summary.Section
        title="Test details and results"
        actions={
          <Link to="..#test-details">
            Edit <VisuallyHidden>Test details and results</VisuallyHidden>
          </Link>
        }
      >
        <Table data-testid="result_values">
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>WBC (10⁹/L)</th>
              <th>Neutrophils (10⁹/L)</th>
              <th>Haemoglobin (g/L)</th>
              <th>Platelets (10⁹/L)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <DateTime format={DateTime.FORMAT.DATE} value={timestamp} />
              </td>
              <td>
                <DateTime format={DateTime.FORMAT.TIME} value={timestamp} />
              </td>
              <td className="bold">{manualResult.whiteBloodCells.value}</td>
              <td className="bold">{manualResult.neutrophils.value}</td>
              <td className="bold">{manualResult.haemoglobin.value}</td>
              <td className="bold">{manualResult.platelets.value}</td>
            </tr>
          </tbody>
        </Table>
      </Summary.Section>
      <ButtonGroup>
        <NavigateButton variant={ButtonVariants.SECONDARY} to="../..">
          Cancel
        </NavigateButton>
        <ExecuteButton>Save results</ExecuteButton>
      </ButtonGroup>
    </Card>
  );
};
